import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { momentScenes } from "../../data/phase3Content";
import { useMediaQuery } from "../../hooks/useMedia";
import { scrollToId, focusWaitlist } from "../../lib/scroll";
import { FrostLayer } from "../FrostLayer";
import { Words } from "../Words";

const go = (target) => (target === "join" ? focusWaitlist() : scrollToId(target));

const MomentCard = ({ scene, i, p, still }) => {
  const y = useTransform(p, [0, 1], still ? [0, 0] : [-28, 28]);
  const src = `/images/${scene.base}.webp`;
  return (
    <motion.div
      initial={still ? false : { opacity: 0, y: 48 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.9, delay: i * 0.12, ease: [0.2, 0.7, 0.2, 1] }}
      className="moment-card-w shrink-0 snap-center"
    >
      <article className="spc moment-card relative aspect-[9/16] w-full overflow-hidden rounded-[36px] bg-[#08090D]" data-testid={`moment-scene-${scene.id}`}>
        <motion.div style={{ y }} className="absolute inset-x-0 -bottom-[5%] -top-[5%]">
          <img src={src} alt={scene.alt} loading="lazy" decoding="async" width="848" height="1264" className="spc-img h-full w-full object-cover" />
        </motion.div>
        <FrostLayer src={src} height="50%" />
        <div className="absolute inset-x-0 bottom-0 z-[2] flex flex-col items-center px-5 pb-6 text-center text-white sm:px-6 sm:pb-7">
          <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-white/70">Moment 0{i + 1} · {scene.tag}</p>
          <h3 className="moment-card-title mt-2">{scene.title}</h3>
          <p className="mt-2 text-[14px] leading-relaxed text-white/80 lg:text-[15px]">{scene.body}</p>
          <button type="button" onClick={() => go(scene.target)} data-testid={`moment-${scene.id}-cta`} className="btn-primary mt-4 h-11 px-5 text-[14px]">
            {scene.cta} <ArrowRight size={16} aria-hidden />
          </button>
        </div>
      </article>
    </motion.div>
  );
};

export default function MomentsSection() {
  const ref = useRef(null);
  const desktop = useMediaQuery("(min-width: 1024px)");
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  return (
    <section ref={ref} id="moments" data-testid="section-moments" className="relative bg-[#F7F8FB] pb-24 pt-28 lg:pb-32 lg:pt-36">
      <div className="mx-auto flex w-full max-w-[1320px] flex-wrap items-end justify-between gap-6 px-5 lg:px-10">
        <div>
          <p className="eyebrow">07 · Life happens together</p>
          <h2 className="h-section mt-3 text-[#08090D]" data-testid="moments-headline">
            <Words text="Life Happens" />
            <br />
            <Words text="Together." start={2} className="text-gradient" />
          </h2>
        </div>
        <p className="max-w-[380px] text-[16px] leading-relaxed text-[#4c5064] lg:text-[17px]">
          From spontaneous plans to lifelong memories, every shared moment can open a new possibility.
        </p>
      </div>
      <div
        data-testid={desktop ? "moments-row" : "moments-carousel"}
        className={desktop ? "mt-14 flex justify-center gap-8 px-10 xl:gap-10" : "no-scrollbar mt-10 flex snap-x snap-mandatory gap-4 overflow-x-auto px-[10vw] pb-4"}
      >
        {momentScenes.map((s, i) => <MomentCard key={s.id} scene={s} i={i} p={scrollYProgress} still={!!reduce || !desktop} />)}
      </div>
    </section>
  );
}
